import { useState, useEffect, Fragment } from 'react';
import { Typography } from '../../../../core/components/molecules/Typography';
import { ContentWrapper, LineContent, Lines, Paper } from './variants';
import { criteriaColor } from '../../utils';
import { useBoardConfig } from '../../../../core/hooks/useBoardConfig';
import DropDown from '../../../../core/components/molecules/Dropdown';
import { useEssayAuxiliarStore } from '../../stores/essayAuxiliarStore';
import { Icon } from '../../../../core/components/atoms/Icon';
import InnerHTMLContainer from '../../../../core/components/molecules/InnerHTMLContainer';
import BrainLoadingWrapper from '../../../../core/components/molecules/BrainLoading';

interface IEssayCorrectedPaper {
  correction: number;
  fontSize: 'small' | 'default' | 'large';
  essayText: string;
  essayTitle: string;
  provider: string;
  comments: string;
  type?: string;
  isLoading: boolean;
}

export default function EssayCorrectedPaper({
  correction,
  fontSize,
  essayText,
  provider,
  comments,
  type = 'feedback',
  isLoading,
}: IEssayCorrectedPaper) {
  const { board } = useBoardConfig(Number(correction) || 2);
  const [paperLines, setPaperLines] = useState<number>(0);
  const [paperHeight, setPaperHeight] = useState<number>(900);
  const [highlightedText, setHighlightedText] = useState<any>('');

  const {
    setPaperHeight: setCommentsHeight,
    selected,
    setSelected,
  } = useEssayAuxiliarStore();

  const sizes = {
    default: 16,
    small: 14,
    large: 18,
  };

  const spaces = {
    default: 2,
    small: 1.5,
    large: 2,
  };

  useEffect(() => {
    handlePaperLines();
  }, [essayText, fontSize]);

  useEffect(() => {
    const LINE_HEIGHT = 30;
    let lines = paperLines;

    if (
      (type === 'waiting' && lines < board.lines) ||
      provider === 'redação_online'
    ) {
      lines = board.lines;
      setPaperLines(board.lines);
    }

    setPaperHeight(lines * LINE_HEIGHT + 20);
    setCommentsHeight(lines * LINE_HEIGHT + 20);
  }, [paperLines]);

  useEffect(() => {
    if (provider === 'ferrari') {
      formatHtml();
    }
  }, [essayText]);

  useEffect(() => {
    if (provider === 'ferrari') {
      formatHtml();
    }
  }, [selected]);

  const handlePaperLines = () => {
    const stringEssayArray = essayText ? essayText.split('\n') : [];
    const size = sizes[fontSize];
    const lineMaxChars = board.font[size].lineMaxChars;

    const essayTextArray = stringEssayArray
      .map((item) => {
        let currentSubstring = '';
        const subStrings: string[] = [];
        const words = item.split(' ');

        for (let i = 0; i < words.length; i++) {
          const word = words[i];
          const maxChar = window.innerWidth < 740 ? lineMaxChars/2 : lineMaxChars;

          const enoughSpace =
            currentSubstring.length + word.length + 1 <= maxChar;
          const justEnough =
            currentSubstring.length + word.length <= maxChar;

          if (enoughSpace || justEnough) {
            if (enoughSpace) {
              currentSubstring += word + ' ';
            } else {
              currentSubstring += word;
            }
          } else {
            subStrings.push(currentSubstring.trim());
            currentSubstring = word + ' ';
          }
        }

        subStrings.push(currentSubstring.trim());

        return subStrings;
      })
      .flat();

    setPaperLines(essayTextArray.length);

    return essayTextArray;
  };

  function formatHtml() {
    if (essayText && comments) {
      const comentarios = JSON.parse(comments);
      const text = essayText + '';
      const newStatementArray: (string | JSX.Element)[] = [];
      let last = 0;
      const c = [...comentarios];

      if (c.length) {
        c.sort((a, b) => (a.start || 0) - (b.start || 0)).forEach((comment) => {
          if (comment.start && comment.start >= (last || 0)) {
            newStatementArray.push(text.slice(last, comment.start));
            const color = criteriaColor(comment.criteriaId);

            //Se der certo, mudar estilo para variant com as cores e o shadow level
            newStatementArray.push(
              <Fragment>
                <span
                  onClick={() =>
                    setSelected(comment.id === selected ? null : comment.id)
                  }
                  className={`z-[100] cursor-pointer text-[#fff]`}
                  data-tooltip-id={`correction-pop-${comment.id}`}
                  style={{
                    background: color,
                    boxShadow:
                      comment.id === selected
                        ? 'rgba(0, 0, 0, 0.24) 0px 3px 8px'
                        : 'none',
                  }}
                >
                  {text.slice(comment.start, comment.end)}
                </span>
                <DropDown
                  id={`correction-pop-${comment.id}`}
                  offset={10}
                  position='bottom'
                  style={{ zIndex: 500 }}
                >
                  <div className='flex max-w-md flex-row items-start gap-md p-1'>
                    <Icon
                      Icon={comment.isPositive ? 'thumb_up' : 'thumb_down'}
                      variant='rounded'
                      type='material'
                      className='mt-2 text-md'
                    />
                    <Typography className='text-[#fff]'>{comment.text}</Typography>
                  </div>
                </DropDown>
              </Fragment>
            );
          }

          if (comment.start === 0) {
            const color = criteriaColor(comment.criteriaId);

            newStatementArray.push(
              <Fragment>
                <span
                  className='z-[100] text-[#fff]'
                  style={{ background: color }}
                >
                  {text.slice(comment.start, comment.end)}
                </span>
                <DropDown
                  id={`correction-pop-${comment.id}`}
                  offset={20}
                  position='bottom-end'
                  style={{ zIndex: 500 }}
                >
                  <div className='max-w-md p-1'>
                    <Typography className='text-[#fff]'>{comment.text}</Typography>
                  </div>
                </DropDown>
              </Fragment>
            );
          }

          last = comment.end;
        });
      }

      newStatementArray.push(text.slice(last));
      
      setHighlightedText(newStatementArray);
    } else {
      setHighlightedText(essayText);
    }
  }

  function replaceNewlinesWithBR(text: string) {
    const newArray: (string | JSX.Element)[] = [];

    const regex = /\n/; // Matches two consecutive newlines
    const matches = text.split(regex);

    if(matches.length > 1) {
      for(let x = 0; x < matches.length; x++) {
        if(x === matches.length - 1) {
          //Caso seja o ultimo
          newArray.push(matches[x])
        } else {
          newArray.push(matches[x]);
          newArray.push(<br/>)
        }
      }
      
      return newArray;
    }

    return text
  }

  const adjustText = (text: any) => {
    const modifiedReactNode = text.map((node: any) => {
      if (node && typeof node === "string") {
        return replaceNewlinesWithBR(node);
      } else {
        const nodo = { ...node }

        if(node.props) {
          nodo.props = { ...node.props, children: node.props.children }
        }

        return nodo;
      }
    });
    
    const newArray = modifiedReactNode.filter((obj: any) => Object.keys(obj).length !== 0);
    return newArray;
  }
  
  return (
    <Fragment>
      <div>
        {isLoading ? (
          <BrainLoadingWrapper />
        ) : (
          <div className={Paper()} style={{ height: paperHeight }}>
            <div className='absolute z-0 h-full w-full'>
              {[...Array(paperLines)].map((__, i) => (
                <div className={Lines()} key={i}>
                  <div className={LineContent()}>
                    <div className='h-4 w-4 rounded-pill bg-mono-color-lighter' />
                    <Typography
                      size='default'
                      className={
                        i + 1 <= board.lines
                          ? 'text-mono-color-darkest'
                          : 'text-red-500'
                      }
                    >
                      
                    </Typography>
                  </div>
                </div>
              ))}
            </div>

            <div className={ContentWrapper()}>
              {provider === 'ferrari' && (
                <p
                  style={{
                    wordSpacing: spaces[fontSize],
                    fontSize: sizes[fontSize],
                  }}
                  className='px-3 py-1 leading-[30px] text-mono-color-darkest'
                >
                  {highlightedText && adjustText(highlightedText)}
                </p>
              )}
              {provider === 'redação_online' && (
                <InnerHTMLContainer
                  type='essayPaper'
                  data={essayText}
                  className='[&_p]:text px-3 py-1 text-mono-color-darkest [&]:leading-[30px]'
                />
              )}
            </div>
          </div>
        )}
      </div>
    </Fragment>
  );
}
